import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Building2,
  CalendarClock,
  Package,
  Plus,
  MoreHorizontal,
  Search,
  Tag,
  Trash2,
  X,
  QrCode,
  CheckCircle2,
  Download,
  Mail,
  User,
} from "lucide-react";
import QRCode from "qrcode";
import logoCamara from "../assets/LogoCamara.png";
import AsignarCuponModal from "./AsignarCuponModal.tsx";
import {
  guardarCupon,
  obtenerCupones,
  obtenerCuponesConSocios,
  eliminarCupon,
  obtenerCuponesUsados,
} from "../services/cuponService.tsx";

type Cupon = {
  id: number;
  comercio: string;
  descripcion: string;
  descuento: number;
  fechaExpiracion: string;
  codigo: string;
};

type SocioAsignado = {
  socio_id?: string | number;
  socio_nombre?: string;
  socio_dni?: string;
};

type CuponUso = {
  id: number;
  socio_id: number;
  socio_email: string;
  socio_nombre: string;
  cupon_id: number;
  cupon_codigo: string;
  cupon_comercio: string;
  cupon_descuento: number;
  fecha_uso: string;
};

const CuponesGestor: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [cupones, setCupones] = useState<Cupon[]>([]);
  const [filtro, setFiltro] = useState("");
  const [mostrarModal, setMostrarModal] = useState(false);
  const [cuponSeleccionado, setCuponSeleccionado] = useState<Cupon | null>(
    null
  );
  const [asignacionesPorCupon, setAsignacionesPorCupon] = useState<
    Record<number, SocioAsignado[]>
  >({});
  const [qrGenerandoId, setQrGenerandoId] = useState<number | null>(null);
  const [tabActiva, setTabActiva] = useState<"cupones" | "usados">("cupones");
  const [cuponesUsados, setCuponesUsados] = useState<CuponUso[]>([]);
  const [cargandoUsados, setCargandoUsados] = useState(false);

  useEffect(() => {
    const cargarCupones = async () => {
      try {
        const token = await getAccessTokenSilently();
        const cupones = await obtenerCupones(token);
        setCupones(cupones);
        await cargarAsignaciones(token);
      } catch (error) {
        console.error("Error al obtener los cupones:", error);
      }
    };
    cargarCupones();
  }, [getAccessTokenSilently]);

  const agregarCupon = async (nuevoCupon: Omit<Cupon, "id">) => {
    try {
      const token = await getAccessTokenSilently();
      const nuevoCuponConId = await guardarCupon(nuevoCupon, token);
      setCupones([...cupones, nuevoCuponConId]);
      setMostrarModal(false);
    } catch (error) {
      console.error("Error al agregar el cupón:", error);
    }
  };

  const eliminarCuponHandler = async (cuponId: number) => {
    try {
      const token = await getAccessTokenSilently();
      await eliminarCupon(cuponId, token);
      setCupones(cupones.filter((cupon) => cupon.id !== cuponId));
      setAsignacionesPorCupon((prev) => {
        const nuevo = { ...prev };
        delete nuevo[cuponId];
        return nuevo;
      });
    } catch (error) {
      console.error("Error al eliminar el cupón:", error);
    }
  };

  const filtrarCupones = () => {
    return cupones.filter((cupon) =>
      cupon.comercio.toLowerCase().includes(filtro.toLowerCase())
    );
  };

  const descargarQR = async (cupon: Cupon, sociosAsignados: SocioAsignado[]) => {
    try {
      setQrGenerandoId(cupon.id);
      const info = {
        codigo: cupon.codigo,
        comercio: cupon.comercio,
        descripcion: cupon.descripcion,
        descuento: `${cupon.descuento}%`,
        expira: formatearFecha(cupon.fechaExpiracion),
        socios: sociosAsignados.map((s) => ({
          id: s.socio_id,
          nombre: s.socio_nombre,
        })),
        emisor: "Cámara Comercial e Industrial de Bolívar",
      };
      const data = `Cupón SocCam\n${JSON.stringify(info, null, 2)}`;
      const url = await QRCode.toDataURL(data, {
        width: 512,
        margin: 2,
      });

      const canvas = document.createElement("canvas");
      const qrImg = new Image();
      qrImg.src = url;

      qrImg.onload = () => {
        const size = 560;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Fondo blanco
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, size, size);

        // QR centrado
        const qrSize = 520;
        const qrX = (size - qrSize) / 2;
        const qrY = (size - qrSize) / 2;
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

        // Logo en el centro del QR
        const logo = new Image();
        logo.src = logoCamara;
        logo.onload = () => {
          const logoSize = qrSize * 0.18;
          const logoX = size / 2 - logoSize / 2;
          const logoY = size / 2 - logoSize / 2;
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(logoX - 6, logoY - 6, logoSize + 12, logoSize + 12);
          ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);

          const finalUrl = canvas.toDataURL("image/png");
          const link = document.createElement("a");
          link.href = finalUrl;
          link.download = `cupon-${cupon.codigo || cupon.id}-qr.png`;
          link.click();
        };
      };
    } catch (error) {
      console.error("No se pudo generar el QR:", error);
      alert("No se pudo generar el QR. Intenta nuevamente.");
    } finally {
      setQrGenerandoId(null);
    }
  };

  const cargarAsignaciones = async (token?: string) => {
    try {
      const authToken = token || (await getAccessTokenSilently());
      const cuponesConSocios = await obtenerCuponesConSocios(authToken);
      const map: Record<number, SocioAsignado[]> = {};
      cuponesConSocios?.forEach((item: any) => {
        const id = Number(
          item?.cupon?.id ?? item?.id ?? item?.cuponId ?? item?.cupon_id
        );
        if (Number.isFinite(id)) {
          const sociosRaw = item?.socios ?? item?.asignaciones ?? [];
          const unicos: Record<string, SocioAsignado> = {};
          sociosRaw.forEach((s: any) => {
            const sid = String(s?.socio_id ?? s?.id ?? "");
            if (!sid) return;
            unicos[sid] = {
              socio_id: s?.socio_id ?? s?.id,
              socio_nombre: s?.socio_nombre ?? s?.nombre,
              socio_dni: s?.socio_dni ?? s?.dni,
            };
          });
          map[id] = Object.values(unicos);
        }
      });
      setAsignacionesPorCupon(map);
    } catch (error) {
      console.error("Error al obtener asignaciones:", error);
    }
  };

  useEffect(() => {
    if (!cuponSeleccionado) return;
    cargarAsignaciones();
  }, [cuponSeleccionado]);

  useEffect(() => {
    if (tabActiva === "usados") {
      cargarCuponesUsados();
    }
  }, [tabActiva]);

  const cargarCuponesUsados = async () => {
    setCargandoUsados(true);
    try {
      const token = await getAccessTokenSilently();
      const data = await obtenerCuponesUsados(token);
      setCuponesUsados(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error al obtener cupones usados:", error);
    } finally {
      setCargandoUsados(false);
    }
  };

  const exportarCSV = () => {
    if (cuponesUsados.length === 0) return;
    const headers = ["Email", "Nombre", "Comercio", "Código", "Descuento", "Fecha de uso"];
    const rows = cuponesUsados.map((u) => [
      u.socio_email,
      u.socio_nombre,
      u.cupon_comercio,
      u.cupon_codigo,
      `${u.cupon_descuento}%`,
      new Date(u.fecha_uso).toLocaleString("es-AR"),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `cupones-usados-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const cuponesFiltrados = filtrarCupones();

  const formatearFecha = (fecha: string) => {
    const date = new Date(fecha);
    if (Number.isNaN(date.getTime())) return "Fecha no válida";
    return date.toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const obtenerEstado = (fecha: string) => {
    const date = new Date(fecha);
    if (Number.isNaN(date.getTime())) {
      return { etiqueta: "Sin fecha", estilos: "bg-gray-100 text-gray-700" };
    }

    const diffDias = Math.ceil(
      (date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDias < 0) {
      return { etiqueta: "Expirado", estilos: "bg-red-100 text-red-700" };
    }
    if (diffDias <= 7) {
      return {
        etiqueta: `Por vencer (${diffDias} días)`,
        estilos: "bg-amber-100 text-amber-800",
      };
    }
    return { etiqueta: "Activo", estilos: "bg-emerald-100 text-emerald-700" };
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-6 space-y-2">
        <p className="text-sm uppercase tracking-[0.2em] text-gray-400">
          Marketing & beneficios
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-4xl font-semibold text-gray-900">
            Gestión de Cupones
          </h1>
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
            <Tag className="h-4 w-4" />
            {tabActiva === "cupones" ? `${cuponesFiltrados.length} cupones visibles` : `${cuponesUsados.length} usos registrados`}
          </div>
        </div>
        <p className="text-gray-500">
          Controlá tus campañas con claridad: busca, crea y asigna cupones en un
          solo lugar.
        </p>
      </div>

      <div className="flex gap-2 rounded-2xl border border-gray-100 bg-white/80 p-1.5 shadow-sm backdrop-blur mb-4">
        <button
          onClick={() => setTabActiva("cupones")}
          className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
            tabActiva === "cupones"
              ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-200"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          <Package className="mr-2 inline h-4 w-4" />
          Cupones
        </button>
        <button
          onClick={() => setTabActiva("usados")}
          className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
            tabActiva === "usados"
              ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-200"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          <CheckCircle2 className="mr-2 inline h-4 w-4" />
          Cupones usados
        </button>
      </div>

      {tabActiva === "cupones" && (
        <>
          <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white/80 p-4 shadow-sm backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por comercio"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-11 pr-4 text-gray-700 shadow-inner focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <button
              onClick={() => setMostrarModal(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-200 transition hover:scale-[1.01] hover:shadow-blue-300"
            >
              <Plus className="h-4 w-4" />
              Nuevo cupón
            </button>
          </div>

          <div className="mt-6 grid auto-rows-fr grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {cuponesFiltrados.map((cupon) => {
              const estado = obtenerEstado(cupon.fechaExpiracion);
              const sociosAsignados = asignacionesPorCupon[cupon.id] || [];
              return (
                <div
                  key={cupon.id}
                  className="group relative flex h-full flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-lg shadow-gray-100 transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 opacity-60" />
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-50 text-blue-600">
                        <Package className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Building2 className="h-4 w-4" />
                          <span className="font-medium">{cupon.comercio}</span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 leading-snug line-clamp-2 min-h-[44px]">
                          {cupon.descripcion}
                        </h3>
                      </div>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${estado.estilos}`}
                    >
                      {estado.etiqueta}
                    </span>
                  </div>

                  <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 p-4 text-white shadow-inner">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.12),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.18),transparent_30%)]" />
                    <div className="relative flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-white/80">
                          Cupón
                        </p>
                        <p className="text-3xl font-extrabold">
                          {cupon.descuento}% OFF
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white/80">Código</p>
                        <span className="inline-flex rounded-full bg-white/20 px-3 py-1 text-sm font-semibold">
                          {cupon.codigo || "—"}
                        </span>
                      </div>
                    </div>
                    <div className="relative mt-3 flex items-center gap-2 text-sm text-white/90">
                      <CalendarClock className="h-4 w-4" />
                      <span>
                        Expira: {formatearFecha(cupon.fechaExpiracion)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => setCuponSeleccionado(cupon)}
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                      >
                        Detalle
                      </button>
                      <button
                        onClick={() => descargarQR(cupon, sociosAsignados)}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-100 disabled:cursor-not-allowed"
                        disabled={qrGenerandoId === cupon.id}
                      >
                        {qrGenerandoId === cupon.id ? (
                          "Generando..."
                        ) : (
                          <>
                            <QrCode className="h-4 w-4" />
                            QR
                          </>
                        )}
                      </button>
                    </div>
                    <button
                      onClick={() => eliminarCuponHandler(cupon.id)}
                      className="inline-flex items-center justify-center rounded-lg border border-red-100 bg-red-50 p-2 text-red-600 transition hover:bg-red-100"
                      aria-label="Eliminar cupón"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tabActiva === "usados" && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              Registros de cupones utilizados por socios para reembolsos.
            </p>
            <button
              onClick={exportarCSV}
              disabled={cuponesUsados.length === 0}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Exportar CSV
            </button>
          </div>

          {cargandoUsados ? (
            <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
              <p className="mt-3 text-sm text-gray-500">Cargando registros...</p>
            </div>
          ) : cuponesUsados.length === 0 ? (
            <div className="rounded-2xl border border-gray-100 bg-white px-4 py-12 text-center shadow-sm">
              <CheckCircle2 className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-500">
                No hay cupones usados registrados aún.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/80">
                      <th className="px-4 py-3 font-semibold text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5" />
                          Email
                        </div>
                      </th>
                      <th className="px-4 py-3 font-semibold text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5" />
                          Nombre
                        </div>
                      </th>
                      <th className="px-4 py-3 font-semibold text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5" />
                          Comercio
                        </div>
                      </th>
                      <th className="px-4 py-3 font-semibold text-gray-600">Código</th>
                      <th className="px-4 py-3 font-semibold text-gray-600">Descuento</th>
                      <th className="px-4 py-3 font-semibold text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <CalendarClock className="h-3.5 w-3.5" />
                          Fecha de uso
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {cuponesUsados.map((uso) => (
                      <tr key={uso.id} className="transition hover:bg-blue-50/30">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {uso.socio_email || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {uso.socio_nombre || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {uso.cupon_comercio || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-bold text-indigo-700">
                            {uso.cupon_codigo}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-bold text-indigo-600">
                          {uso.cupon_descuento}%
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {new Date(uso.fecha_uso).toLocaleString("es-AR", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {mostrarModal && (
        <NuevoCuponModal
          onClose={() => setMostrarModal(false)}
          onSubmit={agregarCupon}
          codigosExistentes={cupones.map((c) => c.codigo || "")}
        />
      )}

      {cuponSeleccionado && (
        <AsignarCuponModal
          cuponId={cuponSeleccionado.id}
          sociosAsignados={asignacionesPorCupon[cuponSeleccionado.id] || []}
          onAsignado={() => {
            cargarAsignaciones();
          }}
          onDesasignado={() => {
            cargarAsignaciones();
          }}
          onClose={() => setCuponSeleccionado(null)}
        />
      )}
    </div>
  );
};

interface NuevoCuponModalProps {
  onClose: () => void;
  onSubmit: (cupon: Omit<Cupon, "id">) => void;
  codigosExistentes: string[];
}

const NuevoCuponModal: React.FC<NuevoCuponModalProps> = ({
  onClose,
  onSubmit,
  codigosExistentes,
}) => {
  const [comercio, setComercio] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [descuento, setDescuento] = useState(0);
  const [fechaExpiracion, setFechaExpiracion] = useState("");
  const [codigo, setCodigo] = useState("");

  const [error, setError] = useState("");

  const handleSubmit = () => {
    const codigoNormalizado = codigo.trim().toUpperCase();
    const existe = codigosExistentes
      .map((c) => c.trim().toUpperCase())
      .includes(codigoNormalizado);

    if (existe) {
      setError("Ese código ya existe. Generá otro o modificá el actual.");
      return;
    }

    if (!comercio || !descripcion || descuento <= 0 || !fechaExpiracion || !codigo) {
      setError("Completá todos los campos y asegurate de que el descuento sea mayor a 0.");
      return;
    }

    setError("");
    onSubmit({
      comercio,
      descripcion,
      descuento,
      fechaExpiracion,
      codigo: codigoNormalizado,
    });
  };

  const generarCodigoUnico = () => {
    const existentes = new Set(
      codigosExistentes.map((c) => c.trim().toUpperCase()).filter(Boolean)
    );

    const randomSegment = () =>
      Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);

    let candidato = "";
    for (let i = 0; i < 20; i++) {
      candidato = `CCIB-${randomSegment()}${randomSegment()}`.slice(0, 12);
      if (!existentes.has(candidato)) break;
    }

    setCodigo(candidato);
    setError("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400">
              Nuevo cupón
            </p>
            <h2 className="text-2xl font-semibold text-gray-900">
              Crear beneficio
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-gray-600">Comercio</span>
            <input
              type="text"
              placeholder="Ej: Casa Central"
              value={comercio}
              onChange={(e) => setComercio(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 shadow-inner focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-600">Descripción</span>
            <input
              type="text"
              placeholder="Ej: Descuento en productos"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 shadow-inner focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-600">Descuento (%)</span>
            <input
              type="number"
              placeholder="20"
              value={descuento}
              min={1}
              onChange={(e) => setDescuento(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 shadow-inner focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-600">Fecha de expiración</span>
            <input
              type="date"
              value={fechaExpiracion}
              onChange={(e) => setFechaExpiracion(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 shadow-inner focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>
          <label className="space-y-1 text-sm sm:col-span-2">
            <span className="text-gray-600">Código</span>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Ej: CCIB-20OFF"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 shadow-inner focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <button
                type="button"
                onClick={generarCodigoUnico}
                className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-100"
              >
                Generar
              </button>
            </div>
          </label>
        </div>

        {error && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Guardar cupón
          </button>
        </div>
      </div>
    </div>
  );
};

export default CuponesGestor;
    
